import { useSDK } from '@littlebird/sdk';
import { type ActionImpl, KBarResults } from 'kbar';
import { observer } from 'mobx-react-lite';
import { useMemo } from 'react';

import { Icons } from '@/assets/icons/icons';
import { XStack } from '@/components/ui';
import { cn } from '@/lib/utils';

import { CommandBarItem } from './cmd-bar-item';
import { buildHeaderKinds } from './header-kinds';

export const COMMAND_BAR_BODY_HEIGHT = 430;

type Props = {
  results: (string | ActionImpl)[];
  rootActionId: string | null | undefined;
  query: string;
  sectionTotals: Record<string, number>;
};

export const CommandBarResults = observer(function CmdBarResults({
  results,
  rootActionId,
  query,
  sectionTotals,
}: Props) {
  const { searchStore } = useSDK();
  const headerKinds = useMemo(() => buildHeaderKinds(results), [results]);

  if (!results.length) {
    if (!searchStore.isSearchReady) {
      return (
        <XStack className="h-full justify-center items-center gap-2 text-foreground-subtle px-2">
          <Icons.loader className="size-3.5 animate-spin text-foreground-muted" />
          {searchStore.isIndexing
            ? 'Indexing your data...'
            : 'Preparing search...'}
        </XStack>
      );
    }
    return (
      <XStack className="h-full justify-center items-center text-foreground-subtle px-2">
        No results found
      </XStack>
    );
  }
  return (
    <KBarResults
      items={results}
      maxHeight={COMMAND_BAR_BODY_HEIGHT}
      onRender={({ item, active }) =>
        typeof item === 'string' ? (
          <div
            className={cn(
              'relative flex select-none items-center rounded-md',
              'px-4 pb-1.5 pt-3.5 text-xs font-semibold text-foreground-muted outline-none',
              'data-[disabled=true]:pointer-events-none data-[disabled=true]:opacity-50',
              'data-[selected=true]:bg-surface-active data-[current=true]:bg-surface-active'
            )}
          >
            {headerKinds.has(item) && (
              <span className="mr-1.5 flex-shrink-0 font-normal text-foreground-muted">
                {headerKinds.get(item)}
              </span>
            )}
            <span
              className={cn(
                'truncate',
                headerKinds.has(item) && 'text-foreground-subtle'
              )}
            >
              {item}
            </span>
            {sectionTotals[item] !== undefined && (
              <span className="ml-1.5 font-normal">{sectionTotals[item]}</span>
            )}
          </div>
        ) : (
          <CommandBarItem
            action={item}
            active={active}
            currentRootActionId={rootActionId}
            query={query}
          />
        )
      }
    />
  );
});
